import { useEffect } from 'react';

import { useNavigate, useSearchParams } from 'react-router';

import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';

import { validateSession } from '@enterprise/api';
import { ensureError, isNotDefined } from '@enterprise/core';
import { StaticLoader } from '@enterprise/ui';

import { broadcastAuthLogin } from '@/app/hooks/cross-tab-auth.hook';
import { useApplicationStore } from '@hooks/application/application-store.hook';

const Session = () => {
  const enterpriseApi = useApplicationStore(({ enterpriseApi }) => {
    return enterpriseApi;
  });
  const setLastValidatedAt = useApplicationStore(({ setLastValidatedAt }) => {
    return setLastValidatedAt;
  });
  const setUser = useApplicationStore(({ setUser }) => {
    return setUser;
  });

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  // biome-ignore lint/correctness/useExhaustiveDependencies: only want to run this effect once on mount
  useEffect(() => {
    const failed = (error: Error) => {
      toast({
        description: `Unable to login to Datastream. Please try again. ${error.message}`,
        status: 'error',
        title: 'Login failed',
      });

      queryClient.removeQueries();
      setUser(undefined);

      void navigate('/login');
    };

    const handleCallback = async () => {
      try {
        const result = await validateSession(enterpriseApi);

        if (result.failure) {
          throw new Error('Failed to validate user session');
        }

        if (isNotDefined(result.data)) {
          throw new Error('Failed to validate user session');
        }

        setUser(result.data);
        setLastValidatedAt(Date.now());
        broadcastAuthLogin(result.data);

        const returnTo = searchParams.get('returnTo');
        const destination = returnTo?.startsWith('/') ? returnTo : '/datastream/packagemanager';

        await navigate(destination, { replace: true });
      } catch (e) {
        const error = ensureError(e);

        failed(error);
      }
    };

    void handleCallback();
  }, []);

  return <StaticLoader />;
};

export default Session;
